import React, { useState, useEffect, useRef } from 'react';

// Hours for display (12-hour format)
const HOURS = [
  '12',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
];
const MINUTES = ['00', '15', '30', '45'];

// Convert 12-hour to 24-hour
const to24Hour = (hour, period) => {
  const h = parseInt(hour, 10);
  if (period === 'AM') return h === 12 ? 0 : h;
  return h === 12 ? 12 : h + 12;
};

// Convert 24-hour to 12-hour display
const to12Hour = (hour24) => {
  if (hour24 === 0) return { hour: '12', period: 'AM' };
  if (hour24 < 12)
    return { hour: String(hour24).padStart(2, '0'), period: 'AM' };
  if (hour24 === 12) return { hour: '12', period: 'PM' };
  return { hour: String(hour24 - 12).padStart(2, '0'), period: 'PM' };
};

export const WheelTimePicker = ({ value, onChange, placeholder }) => {
  // Snap minute to nearest 15-min increment
  const snapMinute = (min) => {
    const m = parseInt(min, 10);
    const snapped = Math.round(m / 15) * 15;
    if (snapped === 60) return '00';
    return String(snapped).padStart(2, '0');
  };

  // Parse HH:MM (24-hour) format - ONE FORMAT everywhere
  const parseTime = (timeStr) => {
    if (!timeStr) return { hour: '09', minute: '00', period: 'AM' };

    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const hour24 = parseInt(match[1], 10);
      const { hour, period } = to12Hour(hour24);
      return { hour, minute: snapMinute(match[2]), period };
    }

    return { hour: '09', minute: '00', period: 'AM' };
  };

  const [time, setTime] = useState(() => parseTime(value));
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value) {
      setTime(parseTime(value));
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleHourChange = (hour) => {
    setTime((prev) => ({ ...prev, hour }));
  };

  const handleMinuteChange = (minute) => {
    setTime((prev) => ({ ...prev, minute }));
  };

  const handlePeriodChange = (period) => {
    setTime((prev) => ({ ...prev, period }));
  };

  // Send simple 24-hour format HH:MM - no date, no timezone
  const handleDone = () => {
    const hour24 = to24Hour(time.hour, time.period);
    const formatted = `${String(hour24).padStart(2, '0')}:${time.minute}`;
    onChange(formatted);
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    if (!value && !isOpen) return placeholder || 'Select time';
    return `${time.hour}:${time.minute} ${time.period}`;
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-10 text-xs font-medium rounded-lg border border-gray-light p-2 bg-white-main text-left flex items-center justify-between"
      >
        <span className={value ? 'text-gray-dark' : 'text-gray-400'}>
          {getDisplayValue()}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white-main border border-gray-light rounded-lg shadow-lg p-3 min-w-max">
          <div className="mb-2 text-center text-sm font-semibold text-gray-dark bg-gray-100 py-1 rounded">
            {time.hour}:{time.minute} {time.period}
          </div>

          <div className="flex gap-3">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-dark mb-1 text-center">
                Hour
              </span>
              <div className="h-36 overflow-y-auto border border-gray-light rounded p-1 bg-white-main">
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => handleHourChange(h)}
                    className={`block w-12 py-1.5 text-sm rounded font-medium ${
                      time.hour === h
                        ? 'bg-primary text-white-main'
                        : 'text-gray-dark hover:bg-gray-light'
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-dark mb-1 text-center">
                Min
              </span>
              <div className="h-36 overflow-y-auto border border-gray-light rounded p-1 bg-white-main">
                {MINUTES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handleMinuteChange(m)}
                    className={`block w-12 py-1.5 text-sm rounded font-medium ${
                      time.minute === m
                        ? 'bg-primary text-white-main'
                        : 'text-gray-dark hover:bg-gray-light'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-dark mb-1 text-center">
                AM/PM
              </span>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => handlePeriodChange('AM')}
                  className={`px-4 py-2.5 text-sm rounded font-medium ${
                    time.period === 'AM'
                      ? 'bg-primary text-white-main'
                      : 'border border-gray-light text-gray-dark hover:bg-gray-light'
                  }`}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={() => handlePeriodChange('PM')}
                  className={`px-4 py-2.5 text-sm rounded font-medium ${
                    time.period === 'PM'
                      ? 'bg-primary text-white-main'
                      : 'border border-gray-light text-gray-dark hover:bg-gray-light'
                  }`}
                >
                  PM
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDone}
            className="mt-3 w-full py-2 text-sm font-semibold bg-primary text-white-main rounded hover:opacity-90"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export const TimePickerField = WheelTimePicker;
